import React, { useEffect, useState } from 'react';
import {IconButton, TextField, Paper, Slider, Divider, Link} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import RoofingIcon from '@mui/icons-material/Roofing';
import ParkIcon from '@mui/icons-material/Park';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMapContext } from '../../contexts/MapContext';
import { Button } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import SubcatchFill from "../../utils/SubcatchFill";
import Help from "../../utils/Help";
import { useLIDContext } from "../../contexts/LIDContext";
import {RB_COST_RATE, GR_COST_RATE, PLANTER_COST_RATE, RG_COST_RATE} from "./LIDCosts";

const AddLIDPanel = () => {
    const { 
        inpFile,
        userData,
        LIDType, 
        setLIDType,
        devPlanID,
        selectedStormOption,
        handleCloseLIDPanel,
        selectedFeature,
        subcatchmentNameJSON,
        addGIFromBrush,
        setAddGIFromBrush,
        setEditMode,
        setBrushMode,
        setEraserMode,
        setHandMode,
    } = useMapContext();
    const {
        devPlanContent,
        setDevPlanContent,
        brushedFeaturesData,
        brushedSubcatchments,
        setBrushedSubcatchments,
        setBrushedFeaturesData,
    } = useLIDContext();

    const selectedFeatureData = selectedFeature.Data;

    const targetSubcatchments = addGIFromBrush ? Array.from(brushedSubcatchments) : [selectedFeatureData.NAME];

    const lidTypeMapping = {
        RG1: "Rain Garden",
        Planter: "Tree Planter",
        GR1: "Green Roof",
        RB12: "Rain Barrel"
    };

    const [selectedLID, setSelectedLID] = useState(null);
    const [volume, setVolume] = useState('');
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [showSummary, setShowSummary] = useState(false);
    const [editLID, setEditLID] = useState(null); // one of [null, Rain Gardens, Green Roofs, Planters, Rain Barrels]
    const [editFormValue, setEditFormValue] = useState(0);
    const [editFormQuantity, setEditFormQuantity] = useState(0);
    const [clearingInProgress, setClearingInProgress] = useState(false);
    const [addingInProgress, setAddingInProgress] = useState(false);

    // LID Settings
    const [RainGardenSize, setRainGardenSize] = useState(1000); // 1000 sqft default
    const [GreenRoofSize, setGreenRoofSize] = useState(1000);   // 1000 sqft default
    const [RainBarrelSize, setRainBarrelSize] = useState(50);   // 50 gal default


    const [RGSlider, setRGSlider] = useState(0);            // Value of slider [0,100]
    const [GRSlider, setGRSlider] = useState(0);            // Value of slider [0,100]
    const [PlanterSlider, setPlanterSlider] = useState(0);  // Value of slider [0,100]
    const [RBSlider, setRBSlider] = useState(0);            // Value of slider [0,100]
    const [sliderTotal, setSliderTotal] = useState(0);

    const [subcatchArea, setSubcatchArea] = useState(0); // in sq_ft

    const [brushedCost, setBrushedCost] = useState(0);

    const [RGCost, setRGCost] = useState(0);
    useEffect(() => {
        let n = Math.floor((subcatchArea * (RGSlider / 100)) / RainGardenSize);
        let c = (RainGardenSize * RG_COST_RATE) * n;
        setRGCost(c.toFixed(2));
    }, [RGSlider, subcatchArea, RainGardenSize]);

    const [GRCost, setGRCost] = useState(0);
    useEffect(() => {
        let n = Math.floor((subcatchArea * (GRSlider / 100)) / GreenRoofSize);
        let c = (GreenRoofSize * GR_COST_RATE) * n;
        setGRCost(c.toFixed(2));
    }, [subcatchArea, GRSlider, GreenRoofSize]);

    const [PlanterCost, setPlanterCost] = useState(0);
    useEffect(() => {
        let n = Math.floor((subcatchArea * (PlanterSlider / 100)) / 9);
        let c = (9 * PLANTER_COST_RATE) * n;
        setPlanterCost(c.toFixed(2));
    }, [PlanterSlider, subcatchArea]);

    const [RBCost, setRBCost] = useState(0);
    useEffect(() => {
        let n = Math.floor((subcatchArea * (RBSlider / 100)) / (((RainBarrelSize * 0.133681) / 3).toFixed(2)));
        let c = (RainBarrelSize * RB_COST_RATE) * n;
        setRBCost(c.toFixed(2));
    }, [subcatchArea, RBSlider, RainBarrelSize]);

    useEffect(() => {
        // Update the brushed features area
        const updateBrushedArea = () => {
            let costSum = 0;
            for (const subcatch_id in brushedFeaturesData) {
                if (brushedFeaturesData[subcatch_id] === null) continue;
                let brushedArea = brushedFeaturesData[subcatch_id]["AREA"] * 43560;
                if (RGSlider > 0) {
                    let n = Math.floor((brushedArea * (RGSlider / 100)) / RainGardenSize);
                    let c = (RainGardenSize * RG_COST_RATE) * n;
                    costSum += c;
                }
                if (GRSlider > 0) {
                    let n = Math.floor((brushedArea * (GRSlider / 100)) / GreenRoofSize);
                    let c = (GreenRoofSize * GR_COST_RATE) * n;
                    costSum += c;
                }
                if (PlanterSlider > 0) {
                    let n = Math.floor((brushedArea * (PlanterSlider / 100)) / 9);
                    let c = (9 * PLANTER_COST_RATE) * n;
                    costSum += c;
                }
                if (RBSlider > 0) {
                    let n = Math.floor((brushedArea * (RBSlider / 100)) / (((RainBarrelSize * 0.133681) / 3).toFixed(2)));
                    let c = (RainBarrelSize * RB_COST_RATE) * n;
                    costSum += c;
                }
            }
            setBrushedCost(costSum);
        }
        updateBrushedArea();
    }, [brushedFeaturesData,  RGSlider, GRSlider, PlanterSlider, RBSlider, RainGardenSize, GreenRoofSize, RainBarrelSize]);



    useEffect(() => {
        const setArea = () => {
            if (!selectedFeature || !selectedFeature["Data"] || !selectedFeature["Data"]["AREA"]) return null;
            setSubcatchArea(selectedFeature["Data"]["AREA"] * 43560);
        }
        setArea();
    }, [selectedFeature]);

    useEffect(() => {
        setSliderTotal(RGSlider + GRSlider + PlanterSlider + RBSlider);
    }, [RGSlider, GRSlider, PlanterSlider, RBSlider]);

    const DEFAULT_SCALE_FACTOR = 3;
    /*
    * Semi-arbitrary scaling functions that make it easier to select lower values on the sliders
    * Could easily be changed in the future, but for now I like the feel of the values it spits out.
    */
    function scaleSlider(value) {
        let SCALE_FACTOR = DEFAULT_SCALE_FACTOR;
        if (subcatchArea && subcatchArea < 30) SCALE_FACTOR = 2;
        value *= 10
        value = Math.pow(value, SCALE_FACTOR);
        value /= Math.pow(1000, SCALE_FACTOR) / 100;
        return +value.toFixed(4);
    }
    function inverseScaleSlider(value) {
        let SCALE_FACTOR = DEFAULT_SCALE_FACTOR;
        if (subcatchArea && subcatchArea < 30) SCALE_FACTOR = 2;
        value *= Math.pow(1000, SCALE_FACTOR) / 100;
        value = Math.pow(value, 1/SCALE_FACTOR);
        value /= 10;
        return +value.toFixed(2);
    }

    const handleRGSliderChange = (event, newValue) => {
        newValue = scaleSlider(newValue);
        if (sliderTotal + newValue - RGSlider > 100) {
            setRGSlider(RGSlider + (100 - sliderTotal));
            return;
        }
        setRGSlider(newValue);
    }
    const handleGRSliderChange = (event, newValue) => {
        newValue = scaleSlider(newValue);
        if (sliderTotal + newValue - GRSlider > 100) {
            setGRSlider(GRSlider + (100 - sliderTotal));
            return;
        }
        setGRSlider(newValue);

    }
    const handlePlanterSliderChange = (event, newValue) => {
        newValue = scaleSlider(newValue);
        if (sliderTotal + newValue - PlanterSlider > 100) {
            setPlanterSlider(PlanterSlider + (100 - sliderTotal));
            return;
        }
        setPlanterSlider(newValue);

    }
    const handleRBSliderChange = (event, newValue) => {
        newValue = scaleSlider(newValue);
        if (sliderTotal + newValue - RBSlider > 100) {
            setRBSlider(RBSlider + (100 - sliderTotal));
            return;
        }
        setRBSlider(newValue);
    }

    useEffect(() => {
        handleLIDClick('RG1');
    }, []); // Empty dependency array means this runs once when component mounts


    // Update the sliders from the existing LID
    const updateLIDSliders = () => {
        setRGSlider(0);
        setGRSlider(0);
        setPlanterSlider(0);
        setRBSlider(0);
        if (devPlanContent === undefined || selectedFeatureData === null || devPlanContent[selectedFeatureData.NAME] === undefined) {
            return;
        }
        //setSliderTotal(0);
        if (devPlanContent[selectedFeatureData.NAME]["RG1"]) {
            let lid_acres = (devPlanContent[selectedFeatureData.NAME]["RG1"].n_replicate * devPlanContent[selectedFeatureData.NAME]["RG1"].area) / 43560;
            setRainGardenSize(devPlanContent[selectedFeatureData.NAME]["RG1"].area);
            setRGSlider((lid_acres / selectedFeature["Data"]["AREA"]) * 100);
        }
        if (devPlanContent[selectedFeatureData.NAME]["GR1"]) {
            let lid_acres = (devPlanContent[selectedFeatureData.NAME]["GR1"].n_replicate * devPlanContent[selectedFeatureData.NAME]["GR1"].area) / 43560;
            setGreenRoofSize(devPlanContent[selectedFeatureData.NAME]["GR1"].area);
            setGRSlider((lid_acres / selectedFeature["Data"]["AREA"]) * 100);
        }
        if (devPlanContent[selectedFeatureData.NAME]["Planter"]) {
            let lid_acres = (devPlanContent[selectedFeatureData.NAME]["Planter"].n_replicate * devPlanContent[selectedFeatureData.NAME]["Planter"].area) / 43560;
            setPlanterSlider((lid_acres / selectedFeature["Data"]["AREA"]) * 100);
        }
        if (devPlanContent[selectedFeatureData.NAME]["RB12"]) {
            let lid_acres = (devPlanContent[selectedFeatureData.NAME]["RB12"].n_replicate * devPlanContent[selectedFeatureData.NAME]["RB12"].area) / 43560;
            let barrelArea = devPlanContent[selectedFeatureData.NAME]["RB12"].area;
            let barrelVolume = (barrelArea * 3 / 0.133681).toFixed(2);
            setRainBarrelSize(barrelVolume);
            setRBSlider((lid_acres / selectedFeature["Data"]["AREA"]) * 100);
        }
        //setSliderTotal(RGSlider + GRSlider + PlanterSlider + RBSlider);
    }
    useEffect(() => {
        updateLIDSliders();
    }, [devPlanContent, selectedFeatureData, selectedFeature]);

    const handleLIDClick = (type) => {
        setLIDType(type);
    };

    const handleAddClick_one_subcatch = (lids )  => {
        if (lids && inpFile) {
            const user = {
                'user_id': userData.id,
                'dev_plan': devPlanID
            };

            if (!inpFile) {
                console.error('No input file selected');
                setAddingInProgress(false);
                return;
            }

            const base_model = `GLWA_${selectedStormOption}`;

            const requestData = {
                base_model: base_model,
                user: user,
                lids: lids
            };

            console.log("The requestData is: ", requestData);

            fetch('https://2471d2z813.execute-api.us-east-2.amazonaws.com/add-lid', {
                method: 'POST',
                headers: {'Content-Type': 'application/json',},
                body: JSON.stringify(requestData)
            })
              .then(response => response.json())
              .then(data => {
                  console.log('Success:', data);
                  setDevPlanContent(prevContent => {
                      let updatedContent = {...prevContent};
                      for (const lid of lids) {
                          // Case 1: We're deleting it
                          if (lid.n_replicate <= 0 && updatedContent[lid.subcatch_id] !== undefined && updatedContent[lid.subcatch_id][lid.lid_name] !== undefined) {
                              delete updatedContent[lid.subcatch_id][lid.lid_name];

                              // If this is the last LID in the subcatch, we need to remove the subcatch from devPlanContent
                              if (Object.keys(updatedContent[lid.subcatch_id]).length === 0) {
                                  delete updatedContent[lid.subcatch_id];
                              }

                              continue;
                          }

                          // If this is the first time we're adding this LID, we need to add the subcatch to devPlanContent
                          if (updatedContent[lid.subcatch_id] === undefined) {
                              updatedContent[lid.subcatch_id] = {};
                          }

                          // Case 2: Doesn't exist and we're adding it
                          if (updatedContent[lid.subcatch_id][lid.lid_name] === undefined) {
                              updatedContent[lid.subcatch_id] = {
                                  ...updatedContent[lid.subcatch_id],
                                  [lid.lid_name]: {
                                      n_replicate: lid.n_replicate,
                                      area: lid.area
                                  }
                              };
                              continue;
                          }
                          // Case 3: Does exist and we're updating it
                          updatedContent[lid.subcatch_id][lid.lid_name] = {
                              n_replicate: lid.n_replicate,
                              area: lid.area
                          }

                      }

                      return updatedContent;
                  });
                  setClearingInProgress(false);
                  setAddingInProgress(false);
              })
              .catch((error) => {
                  setClearingInProgress(false);
                  setAddingInProgress(false);
                  console.error('Error:', error);
              });

            setVolume('');
            setLIDType('');
        }
    }

    const handleAddClick_multiple_subcatch = () => {
        if (RGSlider === 0 && GRSlider === 0 && PlanterSlider === 0 && RBSlider === 0) {
            //handleClearLID();
            return;
        }
        setAddingInProgress(true);
        if (targetSubcatchments.length > 0 && sliderTotal && LIDType && inpFile) {

            const user = { user_id: userData.id, dev_plan: devPlanID };
            const base_model = `GLWA_${selectedStormOption}`;
            const lids = [];


            for (const subcatch_id of targetSubcatchments) {
                const subcatchArea = brushedFeaturesData[subcatch_id]?.AREA * 43560 ?? 0; // convert to sq feet
                console.log("subcatchArea: ", subcatchArea);
                console.log("brushed features data ", brushedFeaturesData);
                if (RGSlider > 0) {
                    // Add Rain Garden
                    let n = Math.floor((subcatchArea * (RGSlider / 100)) / RainGardenSize);
                    console.log("n: ", n);
                    lids.push({
                        subcatch_id,
                        lid_name: 'RG1',
                        n_replicate: n,
                        area: RainGardenSize.toFixed(2)
                    });
                }
                if (GRSlider > 0) {
                    // Add Green Roofs
                    let n = Math.floor((subcatchArea * (GRSlider / 100)) / GreenRoofSize);
                    lids.push({
                        subcatch_id,
                        lid_name: 'GR1',
                        n_replicate: n,
                        area: GreenRoofSize.toFixed(2)
                    });

                }
                if (PlanterSlider > 0) {
                    // Add Planters
                    let n = Math.floor((subcatchArea * (PlanterSlider / 100)) / 9);
                    lids.push({
                        subcatch_id,
                        lid_name: 'Planter',
                        n_replicate: n,
                        area: 9
                    });

                }
                if (RBSlider > 0) {
                    // Add Rain Barrels
                    let n = Math.floor((subcatchArea * (RBSlider / 100)) / (((RainBarrelSize * 0.133681) / 3).toFixed(2)));
                    lids.push({
                        subcatch_id,
                        lid_name: 'RB12',
                        n_replicate: n,
                        area: ((RainBarrelSize * 0.133681) / 3).toFixed(2)
                    });

                }
            }
            console.log("LIDs to add: ", lids);
            for (const lid of lids) {
                if (lid.area <= 0) {
                    setOpenErrorDialog(true);
                    setVolume('');
                    setAddingInProgress(false);
                    return;
                }
            }

            const requestData = {
                base_model,
                user,
                lids  // <- array of lid definitions
            };

            fetch('https://2471d2z813.execute-api.us-east-2.amazonaws.com/add-lid-multiple', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            })
            .then(response => response.json())
            .then(data => {
                console.log('Success:', data);

                setDevPlanContent(prev => {
                    const updated = { ...prev };
                    lids.forEach(({ subcatch_id, lid_name, n_replicate, area }) => {
                        updated[subcatch_id] = {
                            ...updated[subcatch_id],
                            [lid_name]: {
                                n_replicate,
                                area
                            }
                        };
                    });
                    return updated;
                });
                setAddingInProgress(false);

                setBrushedSubcatchments(new Set());
                setBrushedFeaturesData({});
                setAddGIFromBrush(false);
                setEditMode(false);
                setBrushMode(false);
                setEraserMode(false);
                setHandMode(true);
            })
            .catch(error => {
                console.error('Error adding LIDs:', error);
                setAddingInProgress(false);
            });

            setVolume('');
            setLIDType('');
            //setAddingInProgress(false);
        }
    };

    const handleAddClick = () => {
        if (addGIFromBrush) {
            handleAddClick_multiple_subcatch();
            return;
        }
        console.log("************************************");
        console.log("Adding LID to: " + selectedFeatureData.NAME);
        console.log("Rain Garden intensity: " + RGSlider);
        console.log("Green Roof intensity: " + GRSlider);
        console.log("Planter intensity: " + PlanterSlider);
        console.log("Rain Barrel intensity: " + RBSlider);
        console.log("*************************************");

        let maxArea = selectedFeature["Data"]["AREA"] * 43560; // sq feet
        console.log("Adding LIDs to: " + selectedFeatureData.NAME)

        if (RGSlider === 0 && GRSlider === 0 && PlanterSlider === 0 && RBSlider === 0) {
            handleClearLID();
            return;
        }
        setAddingInProgress(true);

        let lids = [];
        if (RGSlider >= 0) {
            // Add Rain Gardens
            //setLIDType('RG1');
            //setArea(RainGardenSize.toString())
            let n = maxArea * (RGSlider / 100);
            n /= RainGardenSize; // Get quantity
            n = Math.floor(n);
            //setQuantity(n.toString());
            // Done setting variables
            // Add it !!!
            const lid = {
                'subcatch_id': selectedFeatureData.NAME,
                'lid_name': 'RG1',
                'n_replicate': n,
                'area': RainGardenSize //sq feet
            };
            lids.push(lid);
            //handleAddClick_one_subcatch("RG1", volume, RainGardenSize.toString(), n.toString());

        }
        if (GRSlider >= 0) {
            // Add Green Roofs
            //setLIDType('GR1');
            //setArea(GreenRoofSize.toString())
            let n = maxArea * (GRSlider / 100);
            n /= GreenRoofSize; // Get quantity
            n = Math.floor(n);
            //setQuantity(n.toString());
            const lid = {
                'subcatch_id': selectedFeatureData.NAME,
                'lid_name': 'GR1',
                'n_replicate': n,
                'area': GreenRoofSize
            }
            lids.push(lid);
            //handleAddClick_one_subcatch("GR1", volume, GreenRoofSize.toString(), n.toString());

        }
        if (PlanterSlider >= 0) {
            // Add Planters
            //setLIDType('Planter')
            let n = maxArea * (PlanterSlider / 100);
            n /= 9; // planters are 9 sqft
            n = Math.floor(n);
            //setQuantity(n.toString());

            const lid = {
                'subcatch_id': selectedFeatureData.NAME,
                'lid_name': 'Planter',
                'n_replicate': n,
                'area': 9
            }
            lids.push(lid);
            //handleAddClick_one_subcatch("Planter", volume, 9, n.toString());

        }
        if (RBSlider >= 0) {
            // Add Rain Barrels
            //setLIDType('RB12');
            //setVolume(RainBarrelSize.toString());

            let n = maxArea * (RBSlider / 100);
            n /= ((RainBarrelSize * 0.133681) / 3).toFixed(2); // Get quantity
            n = Math.floor(n);
            //setQuantity(n.toString());
            const lid = {
                'subcatch_id': selectedFeatureData.NAME,
                'lid_name': 'RB12',
                'n_replicate': n,
                'area': ((RainBarrelSize * 0.133681) / 3).toFixed(2)
            }
            lids.push(lid);

            //handleAddClick_one_subcatch("RB12", RainBarrelSize, ((RainBarrelSize * 0.133681) / 3).toFixed(2), n.toString());
        }
        if (lids.length > 0) {
            handleAddClick_one_subcatch(lids);
        }
    }


    const handleDeleteLIDRow = (lidType, subcatchmentName, adjusted_area) => {
        console.log(`Trying to delete ${lidType} in ${subcatchmentName}`);

        if (inpFile && userData && userData.id && devPlanID && selectedStormOption) {
    
            const lid = {
                'subcatch_id': subcatchmentName, 
                'lid_name': lidType, 
                'n_replicate': 0, 
                'area': adjusted_area
            };
            const user = { 'user_id': userData.id, 'dev_plan': devPlanID };
            const base_model = `GLWA_${selectedStormOption}`;
            const requestData = { base_model: base_model, user: user, lid: lid };

            console.log("The requestData is: ", requestData);

            fetch('https://2471d2z813.execute-api.us-east-2.amazonaws.com/add-lid', {
                method: 'POST',
                headers: {'Content-Type': 'application/json',},
                body: JSON.stringify(requestData)
            })
            .then(response => response.json())
            .then(data => {
                console.log('Success:', data);
                setDevPlanContent(prevContent => {
                    const updatedContent = { ...prevContent };
                    if (updatedContent[subcatchmentName] && updatedContent[subcatchmentName][lidType]) {
                        delete updatedContent[subcatchmentName][lidType];
                    }
                    // If the subcatchment is now empty, delete it from devPlanContent
                    if (Object.keys(updatedContent[subcatchmentName]).length === 0) {
                        delete updatedContent[subcatchmentName];
                    }
                    return updatedContent;
                });
            })
            .catch((error) => {
                console.error('Error:', error);
            });

            setVolume('');
            setLIDType('');
        } else if (!inpFile) {
            console.error('No inp file associated');
        } else if (!selectedStormOption) {
            console.error('No storm option selected');
        } else {
            console.error('Cannot delete LID row: something is missing');
        }
    };

    const handleClearLIDBrushed = () => {
        let lids = [];
        for (const subcatchmentName of brushedSubcatchments) {
            if (devPlanContent[subcatchmentName] === undefined) continue;
            if (devPlanContent[subcatchmentName]["RG1"]) {
                const lid = {
                    'subcatch_id': subcatchmentName,
                    'lid_name': 'RG1',
                    'n_replicate': 0,
                    'area': RainGardenSize
                }
                lids.push(lid);
                //handleDeleteLIDRow('RG1', selectedFeatureData.NAME, devPlanContent[selectedFeatureData.NAME]["RG1"].area);
            }
            if (devPlanContent[subcatchmentName]["GR1"]) {
                const lid = {
                    'subcatch_id': subcatchmentName,
                    'lid_name': 'GR1',
                    'n_replicate': 0,
                    'area': GreenRoofSize
                }
                lids.push(lid);
                //handleDeleteLIDRow('GR1', selectedFeatureData.NAME, devPlanContent[selectedFeatureData.NAME]["GR1"].area);
            }
            if (devPlanContent[subcatchmentName]["Planter"]) {
                const lid = {
                    'subcatch_id': subcatchmentName,
                    'lid_name': 'Planter',
                    'n_replicate': 0,
                    'area': 9
                }
                lids.push(lid);
                //handleDeleteLIDRow('Planter', selectedFeatureData.NAME, devPlanContent[selectedFeatureData.NAME]["Planter"].area);
            }
            if (devPlanContent[subcatchmentName]["RB12"]) {
                const lid = {
                    'subcatch_id': subcatchmentName,
                    'lid_name': 'RB12',
                    'n_replicate': 0,
                    'area': ((volume * 0.133681) / 3).toFixed(2)
                }
                lids.push(lid);
                //handleDeleteLIDRow('RB12', selectedFeatureData.NAME, devPlanContent[selectedFeatureData.NAME]["RB12"].area);
            }
        }
        const user = { user_id: userData.id, dev_plan: devPlanID };
        const base_model = `GLWA_${selectedStormOption}`;
        const requestData = {
            base_model,
            user,
            lids  // <- array of lid definitions
        };

        fetch('https://2471d2z813.execute-api.us-east-2.amazonaws.com/add-lid-multiple', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestData)
        })
          .then(response => response.json())
          .then(data => {
              console.log('Success:', data);

              setDevPlanContent(prev => {
                  const updatedContent = {...prev};
                  for (const lid of lids) {
                      // Case 1: We're deleting it
                      if (lid.n_replicate <= 0 && updatedContent[lid.subcatch_id] !== undefined && updatedContent[lid.subcatch_id][lid.lid_name] !== undefined) {
                          delete updatedContent[lid.subcatch_id][lid.lid_name];

                          // If this is the last LID in the subcatch, we need to remove the subcatch from devPlanContent
                          if (Object.keys(updatedContent[lid.subcatch_id]).length === 0) {
                              delete updatedContent[lid.subcatch_id];
                          }

                          continue;
                      }

                      // If this is the first time we're adding this LID, we need to add the subcatch to devPlanContent
                      if (updatedContent[lid.subcatch_id] === undefined) {
                          updatedContent[lid.subcatch_id] = {};
                      }

                      // Case 2: Doesn't exist and we're adding it
                      if (updatedContent[lid.subcatch_id][lid.lid_name] === undefined) {
                          updatedContent[lid.subcatch_id] = {
                              ...updatedContent[lid.subcatch_id],
                              [lid.lid_name]: {
                                  n_replicate: lid.n_replicate,
                                  area: lid.area
                              }
                          };
                          continue;
                      }
                      // Case 3: Does exist and we're updating it
                      updatedContent[lid.subcatch_id][lid.lid_name] = {
                          n_replicate: lid.n_replicate,
                          area: lid.area
                      }

                  }

                  return updatedContent;
              });
              setClearingInProgress(false);
              setBrushedSubcatchments(new Set());
              setBrushedFeaturesData({});
          });
    }

    const handleClearLID = () => {
        if (addGIFromBrush) {
            setClearingInProgress(true);
            handleClearLIDBrushed();
            return;
        }
        if (devPlanContent === undefined || devPlanContent[selectedFeatureData.NAME] === undefined) return;
        console.log("Clearing LID");
        setClearingInProgress(true);
        let lids = [];
        if (devPlanContent[selectedFeatureData.NAME]["RG1"]) {
            const lid = {
                'subcatch_id': selectedFeatureData.NAME,
                'lid_name': 'RG1',
                'n_replicate': 0,
                'area': RainGardenSize
            }
            lids.push(lid);
            //handleDeleteLIDRow('RG1', selectedFeatureData.NAME, devPlanContent[selectedFeatureData.NAME]["RG1"].area);
        }
        if (devPlanContent[selectedFeatureData.NAME]["GR1"]) {
            const lid = {
                'subcatch_id': selectedFeatureData.NAME,
                'lid_name': 'GR1',
                'n_replicate': 0,
                'area': GreenRoofSize
            }
            lids.push(lid);
            //handleDeleteLIDRow('GR1', selectedFeatureData.NAME, devPlanContent[selectedFeatureData.NAME]["GR1"].area);
        }
        if (devPlanContent[selectedFeatureData.NAME]["Planter"]) {
            const lid = {
                'subcatch_id': selectedFeatureData.NAME,
                'lid_name': 'Planter',
                'n_replicate': 0,
                'area': 9
            }
            lids.push(lid);
            //handleDeleteLIDRow('Planter', selectedFeatureData.NAME, devPlanContent[selectedFeatureData.NAME]["Planter"].area);
        }
        if (devPlanContent[selectedFeatureData.NAME]["RB12"]) {
            const lid = {
                'subcatch_id': selectedFeatureData.NAME,
                'lid_name': 'RB12',
                'n_replicate': 0,
                'area': ((volume * 0.133681) / 3).toFixed(2)
            }
            lids.push(lid);
            //handleDeleteLIDRow('RB12', selectedFeatureData.NAME, devPlanContent[selectedFeatureData.NAME]["RB12"].area);
        }

        handleAddClick_one_subcatch(lids);

    }

    const renderLIDRow = (lidType, lidData, subcatchmentName) => {
        if (lidData.n_replicate > 0) {
            const getIcon = (type) => {
                switch(type) {
                    case 'RG1': return <LocalFloristIcon />;
                    case 'GR1': return <RoofingIcon />;
                    case 'Planter': return <ParkIcon />;
                    case 'RB12': return <OilBarrelIcon />;
                    default: return null;
                }
            };

            return (
                <div key={`${lidType}-${subcatchmentName}`} style={{ marginBottom: '5px' }}>
                    <Button
                        onClick={() => setSelectedLID(selectedLID === `${lidType}-${subcatchmentName}` ? null : `${lidType}-${subcatchmentName}`)}
                        fullWidth
                        style={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            color: '#94cfec',
                            padding: '5px 10px',
                            backgroundColor: selectedLID === `${lidType}-${subcatchmentName}` ? '#2a3a42' : 'transparent',
                            '&:hover': {
                                backgroundColor: '#2a3a42',
                            },
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                {getIcon(lidType)}
                                <span style={{ marginLeft: '15px', fontSize: '14px' }}>
                                    {lidType === 'RB12' 
                                        ? `${((lidData.area * 3) / 0.133681).toFixed(0)} G (${lidData.n_replicate} count)`
                                        : `${lidData.area} sq ft. (${lidData.n_replicate} count)`
                                    }
                                    {/* {lidData.area} sq ft. ({lidData.n_replicate} count) */}
                                </span>
                            </div>
                            {selectedLID === `${lidType}-${subcatchmentName}` && (
                                <IconButton
                                    size="small"
                                    style={{
                                        color: '#94cfec',
                                        padding: '4px',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteLIDRow(lidType, subcatchmentName, lidData.area);
                                    }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                        </div>
                    </Button>
                </div>
            );
        }
        return null;
    };

    const handleSummaryClick = () => {
        if (showSummary) setShowSummary(false);
        else setShowSummary(true);
    };

    const handleEditSave = () => {
        if (editLID == null) return;
        if (selectedFeature == null || selectedFeature["Data"] == null || selectedFeature["Data"]["AREA"] == null) {
            if (editLID === "Rain Gardens") setRainGardenSize(editFormValue);
            else if (editLID === "Green Roofs") setGreenRoofSize(editFormValue);
            else if (editLID === "Rain Barrels") setRainBarrelSize(editFormValue);
            setEditLID(null);
            return;
        }
        let maxArea = selectedFeature["Data"]["AREA"] * 43560; // sq feet
        if (editLID === "Rain Gardens" && editFormQuantity >= 0 && editFormValue >= 0) {
            if (editFormValue > maxArea) setRainGardenSize(maxArea);
            setRainGardenSize(editFormValue);
            let n = editFormQuantity;
            n *= editFormValue;
            if (n > maxArea) n = maxArea;
            setRGSlider((n / maxArea) * 100);
        }
        else if (editLID === "Green Roofs" && editFormQuantity >= 0 && editFormValue >= 0) {
            if (editFormValue > maxArea) setGreenRoofSize(maxArea);
            setGreenRoofSize(editFormValue);
            let n = editFormQuantity;
            n *= editFormValue;
            if (n > maxArea) n = maxArea;
            setRGSlider((n / maxArea) * 100);
        }
        else if (editLID === "Planters" && editFormQuantity >= 0) {
            if (editFormQuantity * 9 > maxArea) setPlanterSlider(100);
            else setPlanterSlider((editFormQuantity * 9 / maxArea) * 100);
        }
        else if (editLID === "Rain Barrels" && editFormQuantity >= 0 && editFormValue >= 0) {
            setRainBarrelSize(editFormValue);
            let rbsize = (((RainBarrelSize * 0.133681) / 3).toFixed(2));
            let n = editFormQuantity;
            n *= rbsize;
            if (n > maxArea) n = maxArea;
            setRBSlider((n / maxArea) * 100);
        }

        setEditLID(null);
    }

    useEffect(() => {
        const loadEditForm = () => {
            if (selectedFeature == null || selectedFeature["Data"] == null || selectedFeature["Data"]["AREA"] == null) {
                if (editLID === "Rain Gardens") setEditFormValue(RainGardenSize);
                else if (editLID === "Green Roofs") setEditFormValue(GreenRoofSize);
                else if (editLID === "Planters") setEditLID(null);
                else if (editLID === "Rain Barrels") setEditFormValue(RainBarrelSize);
                return;
            }
            let subcatchArea = selectedFeature["Data"]["AREA"] * 43560; // sq feet
            if (editLID === "Rain Gardens") {
                let n = Math.floor((subcatchArea * (RGSlider / 100)) / RainGardenSize);
                setEditFormQuantity(n);
                setEditFormValue(RainGardenSize);
            }
            else if (editLID === "Green Roofs") {
                let n = Math.floor((subcatchArea * (GRSlider / 100)) / GreenRoofSize);
                setEditFormQuantity(n);
                setEditFormValue(GreenRoofSize);
            }
            else if (editLID === "Planters") {
                let n = Math.floor((subcatchArea * (PlanterSlider / 100)) / 9);
                setEditFormQuantity(n);
            }
            else if (editLID === "Rain Barrels") {
                let n = Math.floor((subcatchArea * (RBSlider / 100)) / (((RainBarrelSize * 0.133681) / 3).toFixed(2)));
                setEditFormQuantity(n);
                setEditFormValue(RainBarrelSize);
            }

        }
        loadEditForm();
    }, [editLID])



    if (!devPlanID) {
         return null;
    }
    
    const marks = [
        {
            value: 0,
            label: '0%',
        },
        {
            value: 25,
            label: `${scaleSlider(25).toFixed(1)}%`,
        },
        {
            value: 50,
            label: `${scaleSlider(50).toFixed(1)}%`,
        },
        {
            value: 75,
            label: `${scaleSlider(75).toFixed(1)}%`,
        },
    ]
    const marksNoLabel = [
        {
            value: 0,
        },
        {
            value: 25,
        },
        {
            value: 50,
        },
        {
            value: 75,
        },
    ]

    return (
        <div>
            <Dialog 
                open={openErrorDialog} 
                onClose={() => setOpenErrorDialog(false)}
                PaperProps={{
                    style: {
                        backgroundColor: '#1e2a30',
                        color: '#94cfec',
                    }
                }}
            >
                <DialogTitle>Invalid Quantity</DialogTitle>
                <DialogContent>
                    You can't add negative green infrastructure...
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setOpenErrorDialog(false)}
                        style={{ color: '#94cfec' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            
            <Dialog
              open={editLID != null}
              onClose={() => setEditLID(null)}
              sx={{
                  "& .MuiDialog-container": {
                      alignItems: "flex-end",
                      justifyContent: "flex-start",
                      paddingLeft: "180px",
                      paddingBottom: "20px"
                  }
              }}
              PaperProps={{
                  style: {
                      backgroundColor: 'rgba(0,0,0,0.95)',
                      color: '#94cfec',
                  }
              }}
            >
                <DialogTitle>Edit {editLID}</DialogTitle>
                <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
                    {editLID !== 'Planters' && (
                      <TextField
                      variant="outlined"
                        type="number"
                        label={editLID === 'Rain Barrels' ? "Volume (gallons)" : "Area (sq ft)"}
                        value={editFormValue}
                        onChange={(e) => setEditFormValue(e.target.value)}
                        InputLabelProps={{
                        style: {color: '#94cfec'}
                    }}
                        InputProps={{
                        style: {color: '#94cfec'}
                    }}
                        sx={{
                        marginTop: 2,
                        '& .MuiOutlinedInput-root': {
                        '& fieldset': {borderColor: '#94cfec'},
                        '&:hover fieldset': {borderColor: '#94cfec'},
                        '&.Mui-focused fieldset': {borderColor: '#94cfec'},
                    }
                    }}
                        />
                    )}
                    {!addGIFromBrush &&
                      <TextField
                        variant="outlined"
                        type="number"
                        label={"Quantity"}
                        value={editFormQuantity}
                        onChange={(e) => setEditFormQuantity(e.target.value)}
                        InputLabelProps={{
                        style: {color: '#94cfec'}
                    }}
                        InputProps={{
                        style: {color: '#94cfec'}
                    }}
                        sx={{
                        marginTop: 2,
                        '& .MuiOutlinedInput-root': {
                        '& fieldset': {borderColor: '#94cfec'},
                        '&:hover fieldset': {borderColor: '#94cfec'},
                        '&.Mui-focused fieldset': {borderColor: '#94cfec'},
                    }
                    }}
                        />
                    }
                </DialogContent>
                <DialogActions>
                    
                    <Button
                      onClick={() => handleEditSave()}
                      style={{ color: '#94cfec' }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        
        <div
            style={{
                position: 'absolute',
                bottom: 20,
                left: 50,
                width: '780px',
                // height: '250px',
                maxHeight: '360px',
                height: 'auto',
                backgroundColor: '#071317',
                opacity: 0.9,
                zIndex: 10,
                borderRadius: '5px',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div style={{height: "40px" }}>
                <IconButton
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 20 }}
                  onClick={handleCloseLIDPanel}
                >
                    <CloseIcon style={{ color: '#94cfec' }} fontSize="small" />
                </IconButton>

                {/* <h4 style={{ color: '#94cfec', textAlign: 'left', margin: '5px 0px 0px 10px' }} >
                Add Green Infrastructure to {subcatchmentNameJSON[selectedFeatureData.NAME]}
            </h4> */}
                <h4 style={{position: 'absolute', top: 0, left: 0, zIndex: 20, margin: 12, padding: 0}}>
                    Add Green Infrastructure to {addGIFromBrush
                  ? `${targetSubcatchments.length} subcatchments`
                  : subcatchmentNameJSON[selectedFeatureData.NAME]}
                </h4>
            </div>


            {/* Three-column container */}
            {/* <div style={{ display: 'flex', flex: 1, width: '100%' }}> */}
            <div style={{ 
                display: 'flex', 
                flex: 1, 
                width: '100%', 
                alignItems: 'stretch', 
                minHeight: '240px'
            }}>

                {/*LID Type Tabs*/}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', padding: '5px' }}>
                    <h4 style={{color: '#94cfec', textAlign: 'center', position: 'absolute', bottom: '265px', left: '50px'}}>LID Type</h4>
                    <h4 style={{color: '#94cfec', textAlign: 'center', position: 'absolute', bottom: '265px', left: '215px'}}>% of available area</h4>
                    {!addGIFromBrush && (
                      <h4 style={{color: '#94cfec', textAlign: 'center', position: 'absolute', bottom: '265px', left: '425px'}}>Quantity</h4>
                    )}
                    {/* Rain Garden Row */}
                    <div style={{ display: 'flex', alignItems: 'top', justifyContent: 'space-between', padding: '10px', width: '520px', marginTop: '25px' }}>
                        <h5 style={{width: '120px', display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0'}}><LocalFloristIcon sx={{fontSize: 18}}/> &ensp;  Rain Gardens </h5>
                        <Slider
                          value={typeof RGSlider === 'number' ? inverseScaleSlider(RGSlider) : 0}
                          marks={marksNoLabel}
                          onChange={handleRGSliderChange}
                          step={.01}
                          aria-labelledby="Rain Garden Input Slider"
                          style={{width: '250px', marginLeft: '15px'}}
                        />
                        {!addGIFromBrush && (
                          <h4 onClick={() => {setEditLID("Rain Gardens")}} style={{display: 'flex', textAlign: 'center', alignSelf: 'center', alignItems: 'center', justifyContent: 'end', margin: '0 10px', border: '1px solid #94cfec', borderRadius: '5px', padding: '5px', minWidth: '60px', height: '25px'}}>
                              {Math.floor(subcatchArea * (RGSlider / 100) / RainGardenSize)}</h4>
                        )}
                        <EditIcon style={{cursor: "pointer", color: "#94cfec" }} onClick={() => {setEditLID("Rain Gardens")}}/>
                    </div>
                    <Divider/>
                    {/* Green Roof Row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', width: '520px' }}>
                        <h5 style={{width: '120px',display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0'}}><RoofingIcon sx={{ fontSize: 18 }} /> &ensp;  Green Roofs </h5>
                        <Slider
                          value={typeof GRSlider === 'number' ? inverseScaleSlider(GRSlider) : 0}
                          step={.01}
                          marks={marksNoLabel}
                          onChange={handleGRSliderChange}
                          aria-labelledby="Rain Garden Input Slider"
                          style={{width: '250px', marginLeft: '15px'}}
                        />
                        {!addGIFromBrush && (
                          <h4 onClick={() => {setEditLID("Green Roofs")}} style={{display: 'flex', textAlign: 'center', alignSelf: 'center', alignItems: 'center', justifyContent: 'end', margin: '0 10px', border: '1px solid #94cfec', borderRadius: '5px', padding: '5px', minWidth: '60px', height: '25px'}}>
                              {Math.floor(subcatchArea * (GRSlider / 100) / GreenRoofSize)}</h4>
                        )}
                        <EditIcon style={{cursor: "pointer", color: "#94cfec" }} onClick={() => {setEditLID("Green Roofs")}}/>
                    </div>
                    <Divider/>
                    {/* Tree Planter Row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', width: '520px' }}>
                        <h5 style={{width: '120px',display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0'}}><ParkIcon sx={{ fontSize: 18 }} /> &ensp;  Tree Planters </h5>
                        <Slider
                          value={typeof PlanterSlider === 'number' ? inverseScaleSlider(PlanterSlider) : 0}
                          step={.01}
                          marks={marksNoLabel}
                          onChange={handlePlanterSliderChange}
                          aria-labelledby="Tree Planter Input Slider"
                          style={{width: '250px', marginLeft: '15px'}}
                        />
                        {!addGIFromBrush && (
                          <h4 onClick={() => {setEditLID("Planters")}} style={{display: 'flex', textAlign: 'center', alignSelf: 'center', alignItems: 'center', justifyContent: 'end', margin: '0 10px', border: '1px solid #94cfec', borderRadius: '5px', padding: '5px', minWidth: '60px', height: '25px'}}>
                              {Math.floor(subcatchArea * (PlanterSlider / 100) / 9)}</h4>
                        )}
                        <EditIcon style={{cursor: !addGIFromBrush ? "pointer" : "auto", color: !addGIFromBrush ? "#94cfec" : "#6f6f6f"}} onClick={() => {setEditLID("Planters")}}/>
                    </div>
                    <Divider/>
                    {/* Rain Barrel Row */}
                    <div style={{ display: 'flex', alignItems: 'top', justifyContent: 'space-between', padding: '10px', width: '520px' }}>
                        <h5 style={{width: '120px', display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0'}}><OilBarrelIcon sx={{ fontSize: 18 }} /> &ensp;  Rain Barrels </h5>
                        <Slider
                          value={typeof RBSlider === 'number' ? inverseScaleSlider(RBSlider) : 0}
                          step={.01}
                          marks={marks}
                          onChange={handleRBSliderChange}
                          aria-labelledby="Rain Barrel Input Slider"
                          style={{width: '250px', marginLeft: '15px'}}
                        />
                        {!addGIFromBrush && (
                          <h4 onClick={() => {setEditLID("Rain Barrels")}} style={{display: 'flex', textAlign: 'center', alignSelf: 'center', alignItems: 'center', justifyContent: 'end', margin: '0 10px', border: '1px solid #94cfec', borderRadius: '5px', padding: '5px', minWidth: '60px', height: '25px'}}>
                              {Math.floor(subcatchArea * (RBSlider / 100) / ((RainBarrelSize * 0.133681) / 3).toFixed(2))}</h4>
                        )}
                        <EditIcon style={{cursor: "pointer", color: "#94cfec" }} onClick={() => {setEditLID("Rain Barrels")}}/>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly', padding: '10px', width: '100%' }}>
                        <Button onClick={updateLIDSliders} variant="outlined" style={{height: "30px", width: "150px"}}>
                            Reset
                        </Button>
                          <Button onClick={handleClearLID} disabled={clearingInProgress} color="error" variant="outlined" style={{height: "30px", width: "200px"}}>
                              {clearingInProgress ? "Clearing...": "Clear Development"}
                          </Button>

                    </div>

                </div>

                {/* Summary */}
                {!showSummary && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: "center", padding: '0px', position: 'relative', top: '-30px', height: '225px'}}>
                    <div style={{cursor: "pointer"}} onClick={handleSummaryClick}>
                        <SubcatchFill percentage={sliderTotal} width={150} height={150} />
                    </div>

                      <h5 style={{textAlign: 'center', margin: '0px 0px 10px 0px' }}>
                          {!addGIFromBrush && selectedFeature["Data"]["AREA"] && (
                            `Available Area: ${selectedFeature["Data"]["AREA"]} Acres\n`
                              )}
                          <br/>
                          Coverage: {sliderTotal.toFixed(2)}%
                      </h5>

                      <h5 style={{textAlign: 'center', margin: '0px 0px 10px 0px' }}>
                          {!addGIFromBrush ? (
                            `Estimated Cost: ${Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(parseFloat(RGCost) + parseFloat(GRCost) + parseFloat(PlanterCost) + parseFloat(RBCost))}`
                          ) :
                              `Estimated Cost: ${Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(parseFloat(brushedCost))}`
                          }
                          <Help text={<div>
                              This (rough) estimation is from a <Link color={"#8bd3f5"} href={"https://drive.google.com/file/d/1WmxS6ivlrh0pmxd_Myz4fKr0fwIDkxpm/view?usp=sharing"}>2015 study</Link> funded by the US EPA. <br/>
                              Adjusted for inflation as of May 2025.<br/>
                              Rain Gardens: ${RG_COST_RATE.toFixed(2)} / sqft <br/>
                              Green Roofs: ${GR_COST_RATE.toFixed(2)} / sqft <br/>
                              Planters: ${PLANTER_COST_RATE.toFixed(2)} / sqft <br/>
                              Rain barrels: ${RB_COST_RATE.toFixed(2)} / gallon
                          </div>
                          }/>
                      </h5>

                    <Button onClick={handleAddClick} disabled={(addingInProgress || sliderTotal === 0)} variant="contained" style={{height: "30px", marginTop: (!addGIFromBrush) ? "5px" : "30px"}}>
                        {addingInProgress ? "Updating...":
                          (devPlanContent !== undefined && selectedFeatureData !== null && devPlanContent[selectedFeatureData.NAME] !== undefined && devPlanContent[selectedFeatureData.NAME]) ? "Update Plan" : "Add To Plan"
                        }
                    </Button>
                </div>
                )}

                {/* Third column: Development Plan summary */}
                {showSummary &&
                <div onClick={handleSummaryClick} style={{ flex: 1, padding: '10px', color: '#94cfec' }}>

                    <h5 style={{ color: '#94cfec', margin: '0 0 0 0' }}> Development Plan: </h5>
                    <Paper elevation={3} style={{ 
                        padding: '10px', 
                        backgroundColor: '#1e2a30', 
                        color: '#94cfec', 
                        fontSize: '12px', 
                        maxHeight: '90%', 
                        minHeight: '90%',
                        overflow: 'auto' 
                    }}>
                        {targetSubcatchments.map(subcatch_id =>
                            devPlanContent[subcatch_id] &&
                                Object.entries(devPlanContent[subcatch_id]).map(([lidType, lidData]) =>
                                    renderLIDRow(lidType, lidData, subcatch_id)
                                )
                        )}


                    </Paper>
                </div>
                }
            </div>
        </div>
        </div>
    );
};

export default AddLIDPanel;